import PropTypes from 'prop-types'
import { useId } from '../../hooks/useId'
import styles from './TextField.module.css'

const SIZES = ['lg', 'sm']

export function TextField({
  size = 'lg',
  label,
  labelIcon,
  optional = false,
  prefix,
  suffix,
  assistiveText,
  error = false,
  errorMessage,
  maxLength,
  disabled = false,
  readOnly = false,
  id,
  className = '',
  value,
  ...inputProps
}) {
  const generatedId = useId()
  const fieldId = id || generatedId

  const containerClasses = [
    styles.input__container,
    styles[`input__container--${size}`],
    error ? styles['input__container--error'] : '',
    disabled ? styles['input__container--disabled'] : '',
    readOnly ? styles['input__container--readonly'] : '',
  ]
    .filter(Boolean)
    .join(' ')

  const fieldClasses = [styles.input__field, size === 'sm' ? styles['input__field--sm'] : '']
    .filter(Boolean)
    .join(' ')

  const assistiveContent = error ? (
    <span className={[styles.input__assistive, styles['input__assistive--error']].join(' ')}>
      <span className="material-icons-outlined">error_outline</span>
      {errorMessage}
    </span>
  ) : assistiveText ? (
    <span className={styles.input__assistive}>{assistiveText}</span>
  ) : null

  const currentLength = typeof value === 'string' ? value.length : 0
  const showCount = Boolean(maxLength)

  return (
    <div
      className={[styles.input, readOnly ? styles['input--readonly'] : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      {label && (
        <label htmlFor={fieldId} className={styles.input__label}>
          <span
            className={[styles['input__label-text'], styles[`input__label-text--${size}`]].join(' ')}
          >
            {label}
          </span>
          {labelIcon && <span className={styles['input__label-icon']}>{labelIcon}</span>}
          {optional && <span className={styles['input__label-optional']}>(opcional)</span>}
        </label>
      )}
      <div className={containerClasses}>
        {prefix && <span className={styles.input__prefix}>{prefix}</span>}
        <input
          id={fieldId}
          className={fieldClasses}
          disabled={disabled}
          readOnly={readOnly}
          value={value}
          maxLength={maxLength}
          {...inputProps}
        />
        {suffix && <span className={styles.input__suffix}>{suffix}</span>}
      </div>
      {!readOnly &&
        (assistiveContent || showCount) &&
        (showCount ? (
          <div className={styles['input__assistive-row']}>
            {assistiveContent}
            <span className={styles.input__count}>
              {currentLength}/{maxLength}
            </span>
          </div>
        ) : (
          assistiveContent
        ))}
    </div>
  )
}

TextField.propTypes = {
  size: PropTypes.oneOf(SIZES),
  label: PropTypes.node,
  labelIcon: PropTypes.node,
  optional: PropTypes.bool,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  assistiveText: PropTypes.node,
  error: PropTypes.bool,
  errorMessage: PropTypes.node,
  maxLength: PropTypes.number,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string,
}
